import { EntityRatingCommentCount, Professional } from '@medprev-web-app/api-graphql-client';

import { getProfessionalProceduresFromSearchApi } from './get-professional-procedures-from-search-api.service';
import {
  mapSearchApiCommentCount,
  mapSearchApiProfessional,
  mapSearchApiRatingComment,
} from './map-search-api-professional.util';

import {
  findProfessionalBySlug,
  findProfessionalRatingById,
  findProfessionalRatingCommentsById,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  SearchApiRating,
  SearchApiRatingCommentsOrderBy,
  SearchApiServiceChannel,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { InPersonScheduleSource } from '@/utils/professional/extract-professional-location';
import { RatingComment } from '@/types';
import { getLogger } from '@/logger/logger';

export const PROFESSIONAL_PROFILE_COMMENTS_PAGE_SIZE = 5;

const SERVICE_LOCATIONS_PAGE_SIZE = 50;

export interface ProfessionalProfileFromSearchApi {
  professional: Professional;
  gender: string | null;
  commentsCount: EntityRatingCommentCount;
  initialComments: RatingComment[];
  hasMoreComments: boolean;
  inPersonSchedules: InPersonScheduleSource[];
  hasTelehealth: boolean;
}

export interface GetProfessionalProfileCommentsResult {
  comments: RatingComment[];
  hasMore: boolean;
}

export async function getProfessionalProfileFromSearchApi(
  slug: string,
): Promise<ProfessionalProfileFromSearchApi> {
  const professional = await findProfessionalBySlug(slug);

  const [rating, comments, inPerson] = await Promise.all([
    findRatingOrNull(professional.id),
    findFirstCommentsPageOrEmpty(professional.id),
    findServiceChannelPlaces(professional.id, SearchApiServiceChannel.IN_PERSON),
  ]);

  return {
    professional: mapSearchApiProfessional(professional, rating),
    gender: professional.gender ?? null,
    commentsCount: mapSearchApiCommentCount(rating),
    initialComments: comments.comments,
    hasMoreComments: comments.hasMore,
    inPersonSchedules: inPerson.flatMap((group) => group.places.map((place) => place.schedule)),
    hasTelehealth: (professional.serviceChannels ?? []).includes(SearchApiServiceChannel.TELEHEALTH),
  };
}

async function findServiceChannelPlaces(professionalId: string, serviceChannel: SearchApiServiceChannel) {
  try {
    const { procedures } = await getProfessionalProceduresFromSearchApi({
      professionalId,
      serviceChannel,
      limit: SERVICE_LOCATIONS_PAGE_SIZE,
    });

    return procedures;
  } catch (error) {
    getLogger('GetProfessionalProfileFromSearchApi').error('Error fetching the professional service places', {
      professionalId,
      serviceChannel,
      error,
    });

    return [];
  }
}

export async function getProfessionalProfileComments(
  professionalId: string,
  page: number,
  orderBy: SearchApiRatingCommentsOrderBy = SearchApiRatingCommentsOrderBy.NEWEST,
): Promise<GetProfessionalProfileCommentsResult> {
  const { comments, hasMore } = await findProfessionalRatingCommentsById(professionalId, {
    page,
    limit: PROFESSIONAL_PROFILE_COMMENTS_PAGE_SIZE,
    orderBy,
  });

  return { comments: comments.map((comment) => mapSearchApiRatingComment(comment)), hasMore };
}

async function findFirstCommentsPageOrEmpty(
  professionalId: string,
): Promise<GetProfessionalProfileCommentsResult> {
  try {
    return await getProfessionalProfileComments(professionalId, 1);
  } catch (error) {
    getLogger('GetProfessionalProfileFromSearchApi').warn('Error fetching the professional comments', {
      professionalId,
      error,
    });

    return { comments: [], hasMore: false };
  }
}

async function findRatingOrNull(professionalId: string): Promise<SearchApiRating | null> {
  try {
    return await findProfessionalRatingById(professionalId);
  } catch (error) {
    getLogger('GetProfessionalProfileFromSearchApi').warn('Error fetching the professional rating', {
      professionalId,
      error,
    });

    return null;
  }
}
